import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
  email: string;
  showRequirements?: boolean;
}

interface PasswordRequirement {
  label: string;
  test: (password: string, email?: string) => boolean;
}

export default function PasswordStrengthIndicator({
  password,
  email,
  showRequirements = true
}: PasswordStrengthIndicatorProps) {
  const [strength, setStrength] = useState(0);
  const [strengthLabel, setStrengthLabel] = useState('');
  const [strengthColor, setStrengthColor] = useState('');

  const requirements: PasswordRequirement[] = [
    {
      label: 'At least 14 characters',
      test: (pwd) => pwd.length >= 14
    },
    {
      label: 'Contains uppercase letter',
      test: (pwd) => /[A-Z]/.test(pwd)
    },
    {
      label: 'Contains lowercase letter',
      test: (pwd) => /[a-z]/.test(pwd)
    },
    {
      label: 'Contains number',
      test: (pwd) => /\d/.test(pwd)
    },
    {
      label: 'Contains special character (!@#$%^&* etc.)',
      test: (pwd) => /[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=\s]/.test(pwd)
    },
    {
      label: 'Only supported characters',
      test: (pwd) => pwd.length === 0 || /^[a-zA-Z0-9\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=\s]+$/.test(pwd)
    },
    {
      label: "Doesn't contain email parts",
      test: (pwd, email) => {
        if (!email) return true;
        const emailParts = email.toLowerCase().split(/[@.]/);
        return !emailParts.some(part =>
          part.length > 2 && pwd.toLowerCase().includes(part)
        );
      }
    }
  ];

  useEffect(() => {
    if (!password) {
      setStrength(0);
      setStrengthLabel('');
      setStrengthColor('');
      return;
    }

    const requirementsMet = requirements.filter(req =>
      req.test(password, email)
    ).length;

    const strengthScore = (requirementsMet / requirements.length) * 100;

    if (strengthScore < 33) {
      setStrength(strengthScore);
      setStrengthLabel('Weak');
      setStrengthColor('bg-red-500');
    } else if (strengthScore < 66) {
      setStrength(strengthScore);
      setStrengthLabel('Fair');
      setStrengthColor('bg-yellow-500');
    } else if (strengthScore < 100) {
      setStrength(strengthScore);
      setStrengthLabel('Good');
      setStrengthColor('bg-blue-500');
    } else {
      setStrength(100);
      setStrengthLabel('Strong');
      setStrengthColor('bg-green-500');
    }
  }, [password, email]);

  if (!password && !showRequirements) return null;

  return (
    <div className="mt-2 space-y-2">
      {password && (
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-600">Password strength</span>
            {strengthLabel && (
              <span className={`text-xs font-medium ${
                strengthColor === 'bg-green-500' ? 'text-green-600' :
                strengthColor === 'bg-blue-500' ? 'text-blue-600' :
                strengthColor === 'bg-yellow-500' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {strengthLabel}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${strengthColor}`}
              style={{ width: `${strength}%` }}
            />
          </div>
        </div>
      )}

      {showRequirements && password && (
        <div className="space-y-1 pt-1">
          {requirements.map((req, index) => {
            const isMet = req.test(password, email);
            return (
              <div
                key={index}
                className={`flex items-center space-x-1.5 text-xs transition-all duration-200 ${
                  isMet ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {isMet ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : (
                  <XCircle className="w-3.5 h-3.5" />
                )}
                <span>{req.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
